// REAL-TIME GPT-4 EMPLOYEE CHATBOT SERVICE - frontend/src/services/chatbot.service.ts
import { employeeService, Employee } from './employee.service';

// Re-export Employee interface for convenience
export type { Employee } from './employee.service';

export interface ChatMessage {
  id: string;
  type: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  employeeData?: Employee;
  suggestions?: string[];
  isTyping?: boolean;
}

export interface ChatSession {
  id: string;
  messages: ChatMessage[];
  context: ChatContext;
  startTime: Date;
  lastActivity: Date;
}

export interface ChatContext {
  lastQueriedEmployee?: Employee;
  recentQueries: string[];
  sessionStats: {
    employeesQueried: number;
    totalQueries: number;
  };
}

export interface ChatResponse {
  success: boolean;
  message: ChatMessage;
  context?: any;
  error?: string;
}

class ChatBotService {
  private readonly API_KEY = process.env.REACT_APP_OPENAI_API_KEY || '';
  private readonly API_BASE_URL = 'https://api.openai.com/v1/chat/completions';
  private currentSession: ChatSession | null = null;
  private messageIdCounter = 0;

  // 🎯 SYSTEM PROMPT - Defines the chatbot's personality and capabilities
  private readonly SYSTEM_PROMPT = `You are an AI assistant for PHOENICIAN TECHNICAL SERVICES (PTS) employee management system.

🔧 YOUR CAPABILITIES:
- Access to complete employee database with 50+ fields per employee
- Real-time employee data retrieval by employee number, name, or ID
- Detailed employee information including personal, work, and document details
- Smart search and data analysis capabilities

📊 AVAILABLE EMPLOYEE DATA FIELDS:
Core Info: Name, Employee Number, Designation, Department, Email, Phone, Join Date
Personal: Date of Birth, Nationality, Gender, Preferred Name, First Name, Last Name
Contact: Mobile Number, Work Number, Work Email
Work Details: Hiring Date, Work Week, Work Timing, Probation End Date, Salary
Documents: Emirates ID (Number & Expiry), Passport (Number & Expiry), Visa (File Number, UID, Expiry), Labour Card
Line Manager: Manager ID, Name, Department
Status: Currently Insured, Overtime Access, Has Overtime
System: Created By, Created On, Last Updated

🎯 YOUR PERSONALITY:
- Professional but friendly tone
- Concise yet comprehensive responses
- Always verify employee numbers/IDs before searching
- Provide structured, easy-to-read information
- Offer helpful suggestions for next actions

📋 RESPONSE FORMAT:
- Start with employee name and ID for confirmation
- Use bullet points for easy reading
- Highlight important information (expiring documents, missing data)
- Always end with relevant suggestions

🚨 IMPORTANT RULES:
1. NEVER share sensitive personal data unless specifically requested
2. Always confirm employee identity before sharing detailed information
3. If employee not found, suggest similar names or alternative search methods
4. Protect privacy - don't share salary/personal details in general queries
5. Format dates in DD/MM/YYYY format consistently

EXAMPLE RESPONSES:
User: "Show me employee 1001"
Assistant: "👤 **JOHN SMITH** (EMP1001)
• **Position:** Software Engineer | Engineering Department  
• **Contact:** john.smith@pts.com | +971-50-123-4567
• **Joined:** 15/03/2023 (1 year 3 months)
• **Status:** Active | Has Overtime Access ✅

📄 **Documents Status:**
• Emirates ID: Valid until 18/11/2028 ✅
• Passport: Expires 25/05/2026 ⚠️ (Renew within 18 months)
• Visa: Valid until 10/12/2025 ✅

💡 **Quick Actions:** View full profile | Check attendance | Update documents"

Remember: You have REAL ACCESS to the employee database. When users ask for employee information, I will provide you with the actual data from Firebase.`;

  // 🚀 Initialize chat session
  initializeSession(): ChatSession {
    const sessionId = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.currentSession = {
      id: sessionId,
      messages: [
        {
          id: this.generateMessageId(),
          type: 'system',
          content: "👋 **Hello! I'm your PTS Employee Assistant.**\n\nI can help you find detailed information about any employee. Just provide:\n• Employee number (e.g., EMP1001)\n• Employee name\n• Any specific questions about employee data\n\n💡 **Try asking:** \"Show me employee 1001\" or \"Find John Smith\"",
          timestamp: new Date(),
          suggestions: [
            "Show me employee EMP1001",
            "Find employee by name",
            "List all designations",
            "Check document expiries"
          ]
        }
      ],
      context: {
        recentQueries: [],
        sessionStats: {
          employeesQueried: 0,
          totalQueries: 0
        }
      },
      startTime: new Date(),
      lastActivity: new Date()
    };

    console.log('💬 New chat session initialized:', sessionId);
    return this.currentSession;
  }

  // 🧠 Main chat processing method
  async processMessage(userMessage: string): Promise<ChatResponse> {
    try {
      if (!this.currentSession) {
        this.initializeSession();
      }

      if (!this.API_KEY) {
        throw new Error('OpenAI API key not configured');
      }

      console.log('💬 Processing user message:', userMessage);

      // Add user message to session
      const userMsg: ChatMessage = {
        id: this.generateMessageId(),
        type: 'user',
        content: userMessage,
        timestamp: new Date()
      };
      
      this.currentSession!.messages.push(userMsg);
      this.currentSession!.context.recentQueries.push(userMessage);
      this.currentSession!.context.sessionStats.totalQueries++;

      // 🔍 Analyze user intent and extract employee identifiers
      const employeeContext = await this.extractEmployeeContext(userMessage);
      
      // 🎯 Generate GPT-4 response with employee data context
      const assistantResponse = await this.generateAssistantResponse(
        userMessage, 
        employeeContext,
        this.currentSession!.messages
      );

      // Add assistant message to session
      const assistantMsg: ChatMessage = {
        id: this.generateMessageId(),
        type: 'assistant',
        content: assistantResponse.content,
        timestamp: new Date(),
        employeeData: employeeContext.employee,
        suggestions: assistantResponse.suggestions
      };

      this.currentSession!.messages.push(assistantMsg);
      this.currentSession!.lastActivity = new Date();

      if (employeeContext.employee) {
        this.currentSession!.context.lastQueriedEmployee = employeeContext.employee;
        this.currentSession!.context.sessionStats.employeesQueried++;
      }

      return {
        success: true,
        message: assistantMsg,
        context: employeeContext
      };

    } catch (error) {
      console.error('❌ Chat processing error:', error);
      
      const errorMsg: ChatMessage = {
        id: this.generateMessageId(),
        type: 'assistant',
        content: `❌ **Sorry, I encountered an error:**\n\n${error instanceof Error ? error.message : 'Unknown error occurred'}\n\n💡 **Please try:**\n• Rephrasing your question\n• Using a different employee number\n• Asking a simpler question`,
        timestamp: new Date(),
        suggestions: [
          "Try a different employee number",
          "Search by employee name", 
          "Check system status"
        ]
      };

      if (this.currentSession) {
        this.currentSession.messages.push(errorMsg);
      }

      return {
        success: false,
        message: errorMsg,
        error: error instanceof Error ? error.message : 'Chat processing failed'
      };
    }
  }

  // 🔍 Extract employee context from user message
  private async extractEmployeeContext(message: string): Promise<{
    employee?: Employee;
    searchType: 'number' | 'name' | 'general';
    query: string;
  }> {
    const lowerMessage = message.toLowerCase();
    
    // 🔢 Extract employee number patterns
    const empNumberPatterns = [
      /\b(?:emp|employee)\s*(\d+)/i,
      /\b(\d{3,6})\b/,
      /\bemp(\d+)/i
    ];

    for (const pattern of empNumberPatterns) {
      const match = message.match(pattern);
      if (match) {
        const empNumber = match[1];
        const empId = empNumber.startsWith('EMP') ? empNumber : `EMP${empNumber.padStart(4, '0')}`;
        
        console.log('🔍 Searching for employee by number:', empId);
        
        try {
          const employee = await employeeService.getEmployeeById(empId);
          if (employee) {
            console.log('✅ Employee found:', employee.name);
            return {
              employee,
              searchType: 'number',
              query: empId
            };
          }
        } catch (error) {
          console.error('❌ Error fetching employee:', error);
        }
        
        return {
          searchType: 'number',
          query: empId
        };
      }
    }

    // 👤 Extract employee name patterns
    const namePatterns = [
      /(?:find|search|show|get|lookup)\s+(?:employee\s+)?(.+?)(?:\s|$)/i,
      /(?:employee\s+)(.+?)(?:\s|$)/i,
      /^([A-Za-z\s]{2,30})$/
    ];

    for (const pattern of namePatterns) {
      const match = message.match(pattern);
      if (match) {
        const searchName = match[1].trim();
        
        if (searchName.length >= 2) {
          console.log('🔍 Searching for employee by name:', searchName);
          
          try {
            // Use existing employee service to search by name
            const { employees } = await employeeService.getEmployees({ 
              searchQuery: searchName,
              pageSize: 10 
            });
            
            if (employees.length > 0) {
              console.log(`✅ Found ${employees.length} matching employees`);
              return {
                employee: employees[0], // Return first match
                searchType: 'name',
                query: searchName
              };
            }
          } catch (error) {
            console.error('❌ Error searching employees by name:', error);
          }
          
          return {
            searchType: 'name',
            query: searchName
          };
        }
      }
    }

    return {
      searchType: 'general',
      query: message
    };
  }

  // 🎯 Generate GPT-4 response with employee data
  private async generateAssistantResponse(
    userMessage: string,
    employeeContext: any,
    messageHistory: ChatMessage[]
  ): Promise<{ content: string; suggestions: string[] }> {
    
    // 📊 Prepare context for GPT-4
    let contextMessage = '';
    
    if (employeeContext.employee) {
      const emp = employeeContext.employee;
      contextMessage = `USER QUERY: "${userMessage}"

FOUND EMPLOYEE DATA:
{
  "id": "${emp.id}",
  "name": "${emp.name}",
  "employeeNumber": "${emp.employeeNumber}",
  "designation": "${emp.designation}",
  "department": "${emp.department || 'Not Specified'}",
  "email": "${emp.email || 'Not provided'}",
  "phone": "${emp.phone || 'Not provided'}",
  "workEmail": "${emp.workEmail || 'Not provided'}",
  "joinDate": "${emp.joinDate || 'Not specified'}",
  "hiringDate": "${emp.hiringDate || 'Not specified'}",
  "salary": ${emp.salary || 0},
  "dateOfBirth": "${(emp as any)['Date of Birth'] || 'Not specified'}",
  "nationality": "${emp.nationality || (emp as any)['Nationality'] || 'Not specified'}",
  "gender": "${emp.gender || (emp as any)['Gender'] || 'Not specified'}",
  "emiratesId": "${(emp as any)['Emirates ID No.'] || 'Not provided'}",
  "emiratesIdExpiry": "${(emp as any)['Emirates ID Expiry Date'] || 'Not provided'}",
  "passportNumber": "${emp.passportNumber || 'Not provided'}",
  "passportExpiry": "${emp.passportExpiry || 'Not provided'}",
  "visaFileNumber": "${(emp as any)['Residency Visa File Number'] || 'Not provided'}",
  "visaUID": "${(emp as any)['Residency Visa UID'] || 'Not provided'}",
  "visaExpiry": "${(emp as any)['Residency Visa Expiry Date'] || 'Not provided'}",
  "hasOvertime": ${emp.hasOvertime || false},
  "hasOvertimeAccess": ${emp.hasOvertimeAccess || false},
  "currentlyInsured": ${emp.currentlyInsured || false},
  "lineManagerName": "${emp.lineManagerName || 'Not assigned'}",
  "workTiming": "${emp.workTiming || 'Default (09:00 AM - 06:00 PM)'}",
  "createdBy": "${emp.createdBy || 'Unknown'}",
  "createdOn": "${emp.createdOn || 'Unknown'}"
}

Please provide a comprehensive, well-formatted response about this employee based on the user's query.`;
    } else if (employeeContext.searchType === 'number') {
      contextMessage = `USER QUERY: "${userMessage}"

SEARCH RESULT: Employee with number "${employeeContext.query}" was NOT FOUND in the database.

Please provide a helpful response suggesting alternatives or similar employee numbers.`;
    } else if (employeeContext.searchType === 'name') {
      contextMessage = `USER QUERY: "${userMessage}"

SEARCH RESULT: No employee found with name "${employeeContext.query}".

Please provide a helpful response suggesting alternatives or asking for more specific search terms.`;
    } else {
      contextMessage = `USER QUERY: "${userMessage}"

This is a general query. Please provide a helpful response based on your capabilities as a PTS employee assistant.`;
    }

    // 🎭 Build conversation context
    const conversationHistory = messageHistory
      .filter(msg => msg.type !== 'system')
      .slice(-6) // Last 3 exchanges
      .map(msg => ({
        role: msg.type === 'user' ? 'user' : 'assistant',
        content: msg.content
      }));

    // 🚀 Call GPT-4
    const response = await fetch(this.API_BASE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.API_KEY}`
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: this.SYSTEM_PROMPT },
          ...conversationHistory,
          { role: 'user', content: contextMessage }
        ],
        max_tokens: 1000,
        temperature: 0.7,
        presence_penalty: 0.1,
        frequency_penalty: 0.1
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`OpenAI API error: ${errorData.error?.message || response.statusText}`);
    }

    const result = await response.json();
    const assistantContent = result.choices[0]?.message?.content?.trim();

    if (!assistantContent) {
      throw new Error('No response generated from GPT-4');
    }

    // 💡 Generate smart suggestions based on context
    const suggestions = this.generateSmartSuggestions(employeeContext, userMessage);

    return {
      content: assistantContent,
      suggestions
    };
  }

  // 💡 Generate contextual suggestions
  private generateSmartSuggestions(employeeContext: any, userMessage: string): string[] {
    const suggestions: string[] = [];

    if (employeeContext.employee) {
      const emp = employeeContext.employee;
      suggestions.push(`Check ${emp.name}'s attendance`);
      suggestions.push(`View ${emp.name}'s documents`);
      suggestions.push(`Update ${emp.name}'s profile`);
      
      if (emp.lineManagerName) {
        suggestions.push(`Show line manager: ${emp.lineManagerName}`);
      }
    } else {
      suggestions.push("Search for different employee");
      suggestions.push("Show all employees");
      suggestions.push("Find by designation");
      suggestions.push("Check recent updates");
    }

    return suggestions.slice(0, 4); // Max 4 suggestions
  }

  // 🔧 Utility methods
  private generateMessageId(): string {
    return `msg_${Date.now()}_${++this.messageIdCounter}`;
  }

  getCurrentSession(): ChatSession | null {
    return this.currentSession;
  }

  clearSession(): void {
    this.currentSession = null;
    this.messageIdCounter = 0;
    console.log('💬 Chat session cleared');
  }

  // 🏥 Test connection
  async testConnection(): Promise<{ success: boolean; message: string }> {
    try {
      if (!this.API_KEY) {
        return {
          success: false,
          message: 'OpenAI API key not configured'
        };
      }

      const response = await fetch(this.API_BASE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.API_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o',
          messages: [
            {
              role: 'user',
              content: 'Respond with "ChatBot service ready" if you can see this message.'
            }
          ],
          max_tokens: 10
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          message: `API Error: ${errorData.error?.message || response.statusText}`
        };
      }

      // Test employee service connection
      const employeeTest = await employeeService.testConnection();
      
      return {
        success: true,
        message: `ChatBot service ready and connected. Employee service: ${employeeTest.success ? 'Connected' : 'Error'}`
      };

    } catch (error) {
      return {
        success: false,
        message: `Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }
}

export const chatBotService = new ChatBotService();